import clsx from 'clsx'
import { Button, Tooltip, Typography } from '@mui/material'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { Link } from 'react-router-dom'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import useStyles from './_styles'
import { PlannedConsultationCardProps } from './_props'
import { getPatientBirthLabel, getPatientSexLabel, getCivilityLabel, getExpertiseText } from '../../Patient/History/_utils'
import { AttachementFilledIcon, DeleteRoundedIcon, EditWhiteIcon } from '../../../assets/icons/icons'
import { getDateInfo } from '../../../utils/dates'
import { useState } from 'react'
import dayjs from 'dayjs'
import PlannedConsultationDeleteDialog from './PlannedConsultationDeleteDialog'

const PlannedConsultationCard = ({ consultation, isPreview }: PlannedConsultationCardProps) => {
    const classes = useStyles()
    const { patient, requiredExpert, nbDocuments, date } = consultation
    const birthDate = patient?.birthDate ?? ''
    const patientAgeText = `${patient?.age ?? 0} ${patient?.age === 1 ? 'an' : 'ans'}`
    const { isOverdue, day, hour } = getDateInfo(date)
    const attachementLabel =
        nbDocuments > 0
            ? `${nbDocuments} ${nbDocuments === 1 ? 'pièce jointe' : 'pièces jointes'}`
            : 'Aucune pièce jointe'
    const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false)

    return (
        <div className={clsx(classes.PlannedConsultationCardWrapper, isPreview && classes.PlannedConsultationCardWrapperPreview)}>
            <PlannedConsultationDeleteDialog
                isOpen={isDeletionDialogOpen}
                consultationId={consultation.id}
                onClose={() => setIsDeletionDialogOpen(false)}
            />

            <Tooltip
                placement="top"
                title={
                    isOverdue
                        ? 'La date prévue de cette téléconsultation est dépassée, vous devez la décaler ou la supprimer.'
                        : ''
                }
            >
                <div
                    className={clsx(
                        classes.PlannedConsultationCard,
                        isOverdue && classes.PlannedConsultationCardOverdue,
                    )}
                >
                    <div className={clsx(
                        classes.PlannedConsultationCardHeader,
                        isOverdue && classes.PlannedConsultationCardHeaderOverdue,
                    )}>
                        {isOverdue ? (
                            <>
                                <div style={{ display: 'flex', gap: '.5rem' }}>
                                    <div className={classes.PlannedConsultationCardHeaderItem}>
                                        <TodayRoundedIcon />
                                        <Typography>{day}</Typography>
                                    </div>
                                    <div className={classes.PlannedConsultationCardHeaderItem}>
                                        <AccessTimeRoundedIcon />
                                        <Typography>{hour}</Typography>
                                    </div>
                                </div>
                                <Button
                                    className={classes.PlannedConsultationCardHeaderDelete}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setIsDeletionDialogOpen(true)
                                    }}
                                >
                                    <DeleteRoundedIcon />
                                </Button>
                            </>
                        ) : (
                            <>
                                <div className={classes.PlannedConsultationCardHeaderItem}>
                                    <TodayRoundedIcon />
                                    <Typography>{day}</Typography>
                                </div>
                                <div className={classes.PlannedConsultationCardHeaderItem}>
                                    <AccessTimeRoundedIcon />
                                    <Typography>{hour}</Typography>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={classes.PlannedConsultationCardContent}>
                        <div className={classes.PlannedConsultationCardContentItem}>
                            <Typography className={classes.PlannedConsultationCardContentItemLabel}>Patient</Typography>
                            <Typography className={classes.PlannedConsultationCardContentItemValue}>
                                {getCivilityLabel(patient.sex)} {patient.fullName}
                            </Typography>
                            <Typography className={classes.PlannedConsultationCardContentItemInfo}>
                                {getPatientBirthLabel(patient.sex)} le {dayjs(birthDate).format('DD/MM/YYYY')} ({patientAgeText}) - Sexe {getPatientSexLabel(patient.sex)}
                            </Typography>
                        </div>
                        <div className={classes.PlannedConsultationCardContentItem}>
                            <Typography className={classes.PlannedConsultationCardContentItemLabel}>Requis</Typography>
                            <Typography className={classes.PlannedConsultationCardContentItemValue}>
                                {requiredExpert?.fullName}
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.PlannedConsultationCardBottom}>
                        <div className={classes.PlannedConsultationCardBottomInfo}>
                            <AttachementFilledIcon className={classes.PlannedConsultationCardBottomInfoIcon} />
                            <Typography className={classes.PlannedConsultationCardBottomInfoLabel}>
                                {attachementLabel}
                            </Typography>
                        </div>
                        <div className={classes.PlannedConsultationCardBottomButtonContainer}>
                            <Button
                                to={`/planned-consultation/${consultation.id}`}
                                component={Link}
                                disableElevation
                                variant="contained"
                                className={clsx(
                                    classes.PlannedConsultationCardBottomButton,
                                    isOverdue && classes.PlannedConsultationCardBottomButtonOverdue,
                                )}
                            >
                                <EditWhiteIcon className={classes.PlannedConsultationCardBottomButtonIcon} />
                                <span className={classes.PlannedConsultationCardBottomButtonLabel}>
                                    {isOverdue ? 'Mettre à jour' : 'Modifier'}
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            </Tooltip>
        </div>
    )
}

export default PlannedConsultationCard
